import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { oktaAuth } from '../authentication/OktaSSO'
import { BaseResponse } from '../types/base-response'
import { is500Error } from '../utils/app-utils'
import { getRequestToken, handleProtectedUnauthorized } from './httpAuth'
import {getClientId} from '../authentication/clientIdService'

const USE_LOCAL_API = false

const LOCAL_URL = 'http://localhost:8090/api'
const DEV_URL = 'https://planner365.bss.dev.pub.gcp/actions/api'

const getBaseApiUrl = () => {
  const currentLocation = window.location.hostname
  if (currentLocation === 'localhost') {
    return USE_LOCAL_API ? LOCAL_URL : DEV_URL
  }

  return `https://${window.location.hostname}/actions/api`
}

const requireAuthentication = (config: AxiosRequestConfig): boolean => !config.url?.includes('qr')

const httpServiceInstance = axios.create({ baseURL: getBaseApiUrl() })

httpServiceInstance.interceptors.request.use(
  async config => {
    // eslint-disable-next-line no-param-reassign
    const headers: Record<string, string | number> = {
      Authorization: `Bearer ${await getRequestToken(oktaAuth, config, requireAuthentication) ?? ''}`,
      Accept: 'application/json',
      'x-api-version': 1.0,
    }

    const clientId = getClientId()
    if (clientId !== null) {
      headers['x-client-id'] = clientId
    }

    // eslint-disable-next-line no-param-reassign
    config.headers = headers
    return config
  },
  error => Promise.reject(error),
)

httpServiceInstance.interceptors.response.use(
  resp => resp,
  error => {
    handleProtectedUnauthorized(error, requireAuthentication)
    const baseResponse = error?.response?.data as BaseResponse
    if (!is500Error(baseResponse)) {
      return Promise.reject(error)
    }
    // eslint-disable-next-line no-param-reassign
    error.response.data.errors = [
      {
        name: baseResponse.title ?? '',
        description: baseResponse.detail ?? '',
      },
    ]
    return Promise.reject(error)
  }
)

const response = <T>(axiosResponse: AxiosResponse<T>) => axiosResponse.data

const actionsApiHttpService = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    httpServiceInstance.get<T>(url, config).then(response),
  post: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    httpServiceInstance.post<T>(url, body, config).then(response),
  put: <T>(url: string, body: unknown, config?: AxiosRequestConfig) =>
    httpServiceInstance.put<T>(url, body, config).then(response),
  patch: <T>(url: string, body: unknown, config?: AxiosRequestConfig) =>
    httpServiceInstance.patch<T>(url, body, config).then(response),
  del: (url: string, config?: AxiosRequestConfig) =>
    httpServiceInstance.delete(url, config).then(response),
  delWithBody: (url: string, body: unknown) =>
    httpServiceInstance.delete(url, { data: body }).then(response),
}

export default actionsApiHttpService
